import { format, parse } from 'date-fns';
import { PolicyOverviewVm, RiskItem } from 'src/app/api';
import { QuotationSummaryResponse } from '../models/model/concretes/shared/quotation-summary.model';
import { DeclarationState, PersonInfo, VehicleInfo } from 'src/app/core/declaration.store';
import { VehicleTypes } from '../enum/vehicle-type.enum';
import { VehicleCategories } from '../enum/vehicle-category.enum';
import { VehicleStates } from '../enum/vehicle-state.enum';
import { RCVP, RCAUT, ASSVEH1, ASSVEH2 } from '../constants/coverage.constants';
import { ANCIRCUL_VEHIC, CATEG_VEHIC, DATEACH_VEHIC, DATENAISS_PPHY, ETAT_VEHIC, ID, ID_PERPHY, ID_VEHIC, IDS_PERPHY, MARQUE_VEHIC, NOM_PPHY, OPT_VEHIC, POWER_VEHIC, PP_PERPHY, PRENOM_PPHY, PROP_VEHIC, RISK_ITEM_PERSON, RISK_ITEM_PHYSICAL_PERSON, RISK_ITEM_VEHICLE, TYPE_VEHIC, VALEUR_VEHIC } from '../constants/risk-item.constants';
import { RiskItemMeaOption } from '../models/model/concretes/shared/risk-item-mea-option.model';

// Fonction qui génère le RiskItems avec `store` passé en paramètre
function getRiskItems(store: any): Record<string, RiskItem> {
  const address = store.declaration().address.addressAutocomplete;

  return {
    PER: {
      order: 1,
      typeCode: 'PER',
      name: 'Personne',
      fields: {
        ADR_RISQUE:
          address?.streetNumber && address?.street
            ? `${address.streetNumber} ${address.street}`
            : address?.place,
        ADR_CODE_POSTAL: address?.zipCode,
        ADR_VILLE: address?.city
      }
    }
  };
}


export function getMeaRiskItems(store: any): RiskItem[] {
  const vehiclesFromStore = store.declaration().vehicles?.vehicles ?? [];
  const personsFromStore = store.declaration().physicalPersons?.persons ?? [];
  const address = store.declaration().address.addressAutocomplete;
  const startEffectiveDate = store.declaration().effect_date;

  let vehRiskItems: RiskItem[] = [];
  let pphyRiskItems: RiskItem[] = [];
  let personRiskItems: RiskItem[] = [];

  // Retourne les codes des garanties optionnelles actives pour un véhicule (RCVP uniquement si choisie explicitement, RCAUT auto pour les EDPM)
  function getVehicleActiveOptions(vehicle: VehicleInfo): RiskItemMeaOption[] {
    const isBike = vehicle.vehicleType === VehicleTypes.Bike || vehicle.vehicleType === VehicleTypes.ElectricBike;
    const isEdpm = vehicle.vehicleType !== null && !isBike;
    const codes: RiskItemMeaOption[] = [];

    if (isBike && vehicle.optionRc === true) {
      codes.push({ code: RCVP, startDate: startEffectiveDate ?? 'NULL', endDate: 'NULL' });
    }
    if (vehicle.optionAssistance1 === true) {
      codes.push({ code: ASSVEH1, startDate: startEffectiveDate ?? 'NULL', endDate: 'NULL' });
    }
    if (vehicle.optionAssistance2 === true) {
      codes.push({ code: ASSVEH2, startDate: startEffectiveDate ?? 'NULL', endDate: 'NULL' });
    }
    if (isEdpm) {
      codes.push({ code: RCAUT, startDate: startEffectiveDate ?? 'NULL', endDate: 'NULL' });
    }

    return codes;
  }

  let order = 1;

  personRiskItems.push({
    order: order++,
    typeCode: RISK_ITEM_PERSON,
    name: 'Personne',
    fields: {
      ADR_RISQUE:
        address?.streetNumber && address?.street
          ? `${address.streetNumber} ${address.street}`
          : address?.place,
      ADR_CODE_POSTAL: address?.zipCode,
      ADR_VILLE: address?.city
    }
  });

  vehiclesFromStore.forEach((vehicle: VehicleInfo) => {
    vehRiskItems.push({
      order: order++,
      typeCode: RISK_ITEM_VEHICLE,
      name: 'Véhicule',
      fields: {
        ID: vehicle.id,
        ID_VEHIC: 'NULL',
        TYPE_VEHIC: vehicle.vehicleType,
        CATEG_VEHIC: vehicle.vehicleCategory,
        ETAT_VEHIC: vehicle.vehicleState,
        DATEACH_VEHIC: vehicle.purchaseDate ?? 'NULL',
        ANCIRCUL_VEHIC: vehicle.firstRegistrationYear ?? 'NULL',
        VALEUR_VEHIC: vehicle.purchaseValue,
        PROP_VEHIC: vehicle.isOwner,
        POWER_VEHIC: vehicle.powerComplianceCertified,
        OPT_VEHIC: getVehicleActiveOptions(vehicle),
        IDS_PERPHY: '[]',
        MARQUE_VEHIC: 'NULL',
        DATEDEBCOUV_VEHIC: startEffectiveDate ?? 'NULL',
        DATEFINCOUV_VEHIC: 'NULL',
        VP_VEHIC: true
      }
    });
  });

  personsFromStore.forEach((person: PersonInfo) => {
    pphyRiskItems.push({
      order: order++,
      typeCode: RISK_ITEM_PHYSICAL_PERSON,
      name: 'Personne physique',
      fields: {
        ID: person.isSubscriber ? 'NULL' : person.id, // Set value to 'NULL' if the person is the subscriber, as it will be postprocessed in the backend
        NOM_PPHY: person.lastName,
        PRENOM_PPHY: person.firstName,
        DATENAISS_PPHY: person.birthDate,
        DATEDEBCOUV_PPHY: startEffectiveDate ?? 'NULL',
        DATEFINCOUV_PPHY: 'NULL',
        PP_PERPHY: true
      }
    });
  });

  return [...personRiskItems, ...vehRiskItems, ...pphyRiskItems];

}

// Génère les RiskItems pour l'étape de création de contrat : uniquement les personnes non protégées, et les véhicules avec leurs conducteurs (IDS_PERPHY), la marque et numéro de série.
export function getDriversRiskItems(store: any): RiskItem[] {
  const vehiclesFromStore = store.declaration().vehicles?.vehicles ?? [];
  const personsFromStore = store.declaration().physicalPersons?.persons ?? [];

  let vehRiskItems: RiskItem[] = [];
  let pphyRiskItems: RiskItem[] = [];

  let order = 1;

  vehiclesFromStore.forEach((vehicle: VehicleInfo) => {
    vehRiskItems.push({
      order: order++,
      typeCode: RISK_ITEM_VEHICLE,
      name: 'Véhicule',
      fields: {
        ID: vehicle.id,
        ID_VEHIC: vehicle.vehicleSerialNumber,
        MARQUE_VEHIC: vehicle.vehicleBrand ?? '',
        IDS_PERPHY: vehicle.drivers.map(driver => driver.id) ?? []
      }
    });
  });

  personsFromStore
    .filter((person: PersonInfo) => person.isProtected === false)
    .forEach((person: PersonInfo) => {
      pphyRiskItems.push({
        order: order++,
        typeCode: RISK_ITEM_PHYSICAL_PERSON,
        name: 'Personne physique',
        fields: {
          ID: person.id,
          NOM_PPHY: person.lastName,
          PRENOM_PPHY: person.firstName,
          DATENAISS_PPHY: person.birthDate,
          PP_PERPHY: false
        }
      });
    });

  return [...vehRiskItems, ...pphyRiskItems];
}

// Fonction qui génère le DeclarationState avec `QuotationSummaryResponse` passé en paramètre
function getDeclarationStateFromQuotationSummary(
  quotationSummary: QuotationSummaryResponse
): DeclarationState {
  const persons = getPersonsFromRiskItems(quotationSummary);

  return {
    email: '',
    address: {
      addressAutocomplete: {
        streetNumber:
          quotationSummary.clientInformations.address1
            .split(' ')
            .slice(0, 1)
            .join(' ') ?? '',
        street: quotationSummary.clientInformations.address1.split(' ')[0] ?? '',
        zipCode: quotationSummary.clientInformations.zipCode,
        city: quotationSummary.clientInformations.addressCity,
      },
      address: quotationSummary.clientInformations.address1,
      city: quotationSummary.clientInformations.addressCity,
      country: null,
      zipCode: quotationSummary.clientInformations.zipCode,
    },

    effect_date: format(
      parse(quotationSummary.requestedStartEffectiveDate, 'yyyy-MM-dd', new Date()),
      'dd/MM/yyyy HH:mm:ss'
    ),

    chosenCoverageType: null, // À définir selon le contexte
    physicalPersons:{ persons: persons },
    vehicles: {vehicles: getVehiclesFromRiskItems(quotationSummary, persons) },
  };
}

function getDeclarationStateFromPolicyOverview(
  policySummary: PolicyOverviewVm
): DeclarationState {
  const persons = getPersonsFromRiskItems(policySummary);
  return {
    email: '',
    address: null,
    effect_date: null,

    chosenCoverageType: null, // À définir selon le contexte
    physicalPersons:{ persons: persons },
    vehicles: {vehicles: getVehiclesFromRiskItems(policySummary, persons) },
  };
}

// Fonction qui retourne un RiskItem spécifique
export function getRiskItem(store: any, key: string): RiskItem | undefined {
  const riskItems = getRiskItems(store);
  return riskItems[key]; // Retourne l'objet si trouvé, sinon `undefined`
}

export function getDeclarationStateFromQuotation(quotationSummary: QuotationSummaryResponse ): DeclarationState {
  return getDeclarationStateFromQuotationSummary(quotationSummary);
}

export function getDeclarationStateFromPolicy(policySummary: PolicyOverviewVm ): DeclarationState {
  return getDeclarationStateFromPolicyOverview(policySummary);
}

function format_date(dateString: string): string {
  let date: Date = new Date();
  if (typeof dateString === 'string') {
    date = parse(dateString, 'dd/MM/yyyy', new Date());
  }

  return format(date, 'yyyy-MM-dd');
}

function getPersonsFromRiskItems(quotationSummary: QuotationSummaryResponse | PolicyOverviewVm): PersonInfo[] {
  const personsRiskItems = quotationSummary.allRiskItemInformations.filter(ri => ri.riskItemTypeCode === RISK_ITEM_PHYSICAL_PERSON);
  return personsRiskItems.map(ri => {
    const values = ri.values.reduce((acc, val) => {
      acc[val.code] = val.value;
      return acc;
    }, {} as Record<string, string>);

    return {
      id: values[ID] ?? '',
      isSubscriber: false,
      isProtected: Boolean(values[PP_PERPHY] ?? false),
      lastName: values[NOM_PPHY] ?? '',
      firstName: values[PRENOM_PPHY] ?? '',
      birthDate: values[DATENAISS_PPHY] ?? '',
    } as PersonInfo;
  });
}

function getVehiclesFromRiskItems(quotationSummary: QuotationSummaryResponse | PolicyOverviewVm, persons: PersonInfo[]): VehicleInfo[] {
  const vehiclesRiskItems = quotationSummary.allRiskItemInformations.filter(ri => ri.riskItemTypeCode === RISK_ITEM_VEHICLE);
  return vehiclesRiskItems.map(ri => {
    const values = ri.values.reduce((acc, val) => {
      acc[val.code] = val.value;
      return acc;
    }, {} as Record<string, string>);
    const selectedOptionCodes = toStringArray(values[OPT_VEHIC]);
    const drivers = toStringArray(values[IDS_PERPHY]);

    return {
      id: values[ID] ?? '',
      vehicleType: toNumericEnum(values[TYPE_VEHIC], VehicleTypes),
      vehicleCategory: toNumericEnum(values[CATEG_VEHIC], VehicleCategories),
      vehicleState: toNumericEnum(values[ETAT_VEHIC], VehicleStates),
      vehicleBrand: values[MARQUE_VEHIC] === 'NULL' ? null : values[MARQUE_VEHIC] ?? null,
      vehicleSerialNumber: values[ID_VEHIC] === 'NULL' ? null : values[ID_VEHIC] ?? null,
      purchaseDate: values[DATEACH_VEHIC] === 'NULL' ? null : values[DATEACH_VEHIC] ?? null,
      firstRegistrationYear: Number(values[ANCIRCUL_VEHIC] ?? ''),
      purchaseValue: Number(values[VALEUR_VEHIC] ?? ''),
      isOwner: Boolean(values[PROP_VEHIC] ?? ''),
      powerComplianceCertified: Boolean(values[POWER_VEHIC] ?? ''),
      optionRc: selectedOptionCodes.includes(RCVP),
      optionRcOriginal: false,
      optionAssistance1: selectedOptionCodes.includes(ASSVEH1),
      optionAssistance2: selectedOptionCodes.includes(ASSVEH2),
      drivers: drivers.map(driverId => persons.find(person => person.id === driverId)).filter((driver): driver is PersonInfo => driver !== undefined),
    } as VehicleInfo;
  });
}

function toNumericEnum<T extends number>(value: string | undefined, enumValues: object): T | null {
  const numericValue = Number(value);
  return Object.values(enumValues).includes(numericValue) ? numericValue as T : null;
}

function toStringArray(value: string | undefined): string[] {
  try {
    const parsedValue: unknown = JSON.parse(value ?? '[]');
    return Array.isArray(parsedValue) && parsedValue.every((item) => typeof item === 'string')
      ? parsedValue
      : [];
  } catch {
    return [];
  }
}
